import { useEffect, useMemo, useState } from 'react'
import { Box, Flex, Input as MantineInput, Transition } from '@mantine/core'
import type { DateValue } from '@mantine/dates'
import { DatePicker } from '@mantine/dates'
import { useClickOutside } from '@mantine/hooks'
import dayjs from 'dayjs'

import { NumberInput } from '@/components/NumberInput/NumberInput'
import type {
  DateFormat,
  SeparateDateInputProps,
} from '@/components/SeparateDateInput/SeparateDateInput.types'
import { Text } from '@/components/Text/Text'

import classes from './SeparateDateInput.module.css'

export function SeparateDateInput({
  containerProps,
  disableFutureDates = false,
  disablePastDates = false,
  disabled = false,
  gap = 10,
  label,
  maxDate,
  minDate,
  onChange,
  value,
  ...props
}: SeparateDateInputProps) {
  const [datePicker, setDatePicker] = useState<boolean>(false)
  const [currentPickerDate, setCurrentPickerDate] = useState<Date | undefined>(
    undefined,
  )
  const [dateBreakdown, setDateBreakdown] = useState<DateFormat>({
    day: undefined,
    month: undefined,
    year: undefined,
  })
  const [controlDay, setControlDay] = useState<HTMLDivElement | null>(null)
  const [controlMonth, setControlMonth] = useState<HTMLDivElement | null>(null)
  const [controlYear, setControlYear] = useState<HTMLDivElement | null>(null)
  const [controlPicker, setControlPicker] = useState<HTMLDivElement | null>(
    null,
  )

  useClickOutside(() => setDatePicker(false), null, [
    controlDay,
    controlMonth,
    controlYear,
    controlPicker,
  ])

  const breakValues = (date: Date | undefined) => ({
    day: date ? dayjs(date).date() : undefined,
    month: date ? dayjs(date).month() + 1 : undefined,
    year: date ? dayjs(date).year() : undefined,
  })

  useEffect(() => {
    setCurrentPickerDate(value)
    setDateBreakdown(breakValues(value))
  }, [value])

  const today = useMemo(() => dayjs().startOf('day'), [])

  const createTimestamp = (date: DateFormat) => {
    const { day, month, year } = date

    const newDate = dayjs(`${year}-${month}-${day}`).toDate()

    if (newDate.toString() === 'Invalid Date') return undefined

    return newDate
  }

  const handleChangeDate = (date: DateValue) => {
    if (!date) return
    onChange?.(date)
    setDatePicker(false)
  }

  const handleChangeInput = (value: number | string, key: string) => {
    const newDate = {
      ...dateBreakdown,
      [key]: !value ? undefined : value,
    }

    const timestamp = createTimestamp(newDate)

    if (!newDate.day && !newDate.month && !newDate.year) {
      onChange?.('')

      return
    }

    if (
      timestamp &&
      newDate.day &&
      newDate.month &&
      newDate.year &&
      newDate.year.toString().length > 3
    ) {
      if (disableFutureDates && dayjs(timestamp).isAfter(today))
        onChange?.(today.toDate())
      else if (disablePastDates && dayjs(timestamp).isBefore(today))
        onChange?.(today.toDate())
      else onChange?.(timestamp)
    }

    setDateBreakdown(newDate)
  }

  const handleBlur = () => {
    if (minDate && dayjs(value).isBefore(minDate)) {
      onChange?.(minDate)
    }

    if (maxDate && dayjs(value).isAfter(maxDate)) {
      onChange?.(maxDate)
    }
  }

  return (
    <Box {...containerProps}>
      {label && (
        <Text align="left" mb={8} variant="body_2_medium_black">
          {label}
        </Text>
      )}

      <MantineInput.Wrapper className={classes.wrapper} mb={16} {...props}>
        <Flex gap={gap} style={{ position: 'relative' }}>
          <NumberInput
            ref={setControlMonth}
            allowDecimal={false}
            disabled={disabled}
            hideControls
            max={12}
            maxLength={2}
            mb={0}
            min={1}
            onBlur={handleBlur}
            onChange={value => handleChangeInput(value, 'month')}
            onClick={() => setDatePicker(true)}
            placeholder="MM"
            thousandSeparator={false}
            value={dateBreakdown.month}
          />

          <NumberInput
            ref={setControlDay}
            allowDecimal={false}
            disabled={disabled}
            hideControls
            max={31}
            maxLength={2}
            mb={0}
            min={1}
            onBlur={handleBlur}
            onChange={value => handleChangeInput(value, 'day')}
            onClick={() => setDatePicker(true)}
            placeholder="DD"
            thousandSeparator={false}
            value={dateBreakdown.day}
          />

          <NumberInput
            ref={setControlYear}
            allowDecimal={false}
            allowLeadingZeros={false}
            disabled={disabled}
            hideControls
            maxLength={4}
            mb={0}
            onBlur={handleBlur}
            onChange={value => handleChangeInput(value, 'year')}
            onClick={() => setDatePicker(true)}
            placeholder="YYYY"
            thousandSeparator={false}
            value={dateBreakdown.year}
          />

          <Transition
            duration={200}
            mounted={datePicker}
            onExited={() => setCurrentPickerDate(value)}
            timingFunction="ease"
            transition="fade"
          >
            {styles => (
              <DatePicker
                ref={setControlPicker}
                className={classes.datePicker}
                classNames={{
                  day: classes.customDay,
                }}
                date={currentPickerDate}
                maxDate={disableFutureDates ? today.toDate() : maxDate}
                minDate={disablePastDates ? today.toDate() : minDate}
                onChange={handleChangeDate}
                onDateChange={setCurrentPickerDate}
                style={{ ...styles }}
                value={currentPickerDate}
              />
            )}
          </Transition>
        </Flex>
      </MantineInput.Wrapper>
    </Box>
  )
}
